import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AttachMoney, Description, ShoppingCart, Warning } from '@mui/icons-material';
import {
  PurchaseOrderPartial,
  RequisitionItem,
  useGetItemsDropdownQuery,
  usePatchPurchaseOrdersByIdUpdateCodesMutation,
} from '../../../redux/vmsApi';
import { calculateVAT, formatCurrency } from '../../../utils/currencyUtil';

const GradientDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
  color: theme.palette.common.white,
  fontWeight: 'bold',
  textAlign: 'center',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: theme.shadows[3],
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.common.white,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));

interface BackOrderAndReceivedComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  itemsReceived: RequisitionItem[];
  backOrderItems: RequisitionItem[];
  selectedPurchaseOrder: PurchaseOrderPartial;
  currentUser?: { roles?: string[] };
}

const BackOrderAndReceivedComparisonDialog: React.FC<BackOrderAndReceivedComparisonDialogProps> = ({
  open,
  onClose,
  itemsReceived,
  backOrderItems,
  selectedPurchaseOrder,
  currentUser,
}) => {
  const isStoresClerk =
    currentUser?.roles?.includes('stores-clerk') || currentUser?.roles?.includes('admin');
  const [editedCodes, setEditedCodes] = useState<Record<string, string>>({});
  const [savedCodes, setSavedCodes] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [updateCodes, { isLoading: isSaving }] = usePatchPurchaseOrdersByIdUpdateCodesMutation();
  const { data: inventoryItems = [] } = useGetItemsDropdownQuery();

  useEffect(() => {
    if (!open) {
      setEditedCodes({});
      setSavedCodes({});
    }
  }, [open, selectedPurchaseOrder.id]);

  const handleCodeChange = (description: string, value: string) => {
    setEditedCodes((prev) => ({ ...prev, [description]: value }));
  };

  const handleAutocompleteChange = (description: string, newValue: any | null) => {
    handleCodeChange(description, String(newValue?.itemCode ?? ''));
  };

  const findBestInventoryMatch = (item: RequisitionItem) => {
    const selectedCode = String(editedCodes[item.description as string] ?? item.itemCode ?? '').trim();
    const itemDescription = String(item.description ?? '').trim().toLowerCase();

    if (selectedCode) {
      const codeMatch = inventoryItems.find(
        (inventoryItem) => String(inventoryItem.itemCode ?? '').trim() === selectedCode
      );
      if (codeMatch) {
        return codeMatch;
      }
    }

    if (!itemDescription) {
      return null;
    }

    const exactDescriptionMatch = inventoryItems.find(
      (inventoryItem) => String(inventoryItem.description ?? '').trim().toLowerCase() === itemDescription
    );

    if (exactDescriptionMatch) {
      return exactDescriptionMatch;
    }

    const startsWithMatch = inventoryItems.find((inventoryItem) =>
      String(inventoryItem.description ?? '').trim().toLowerCase().startsWith(itemDescription)
    );

    if (startsWithMatch) {
      return startsWithMatch;
    }

    const includesMatch = inventoryItems.find((inventoryItem) =>
      String(inventoryItem.description ?? '').trim().toLowerCase().includes(itemDescription)
    );

    if (includesMatch) {
      return includesMatch;
    }

    return inventoryItems.find((inventoryItem) =>
      itemDescription.includes(String(inventoryItem.description ?? '').trim().toLowerCase())
    ) || null;
  };

  const handleSaveCodes = async () => {
    if (!selectedPurchaseOrder.id) {
      return;
    }

    const backOrderItemPatches = backOrderItems
      .filter((item) => item.description)
      .map((item) => {
        const description = item.description as string;
        const editedCode = String(editedCodes[description] ?? '').trim();
        const existingCode = String(item.itemCode ?? '').trim();
        const matchedCode = String(findBestInventoryMatch(item)?.itemCode ?? '').trim();

        return {
          description,
          itemCode: editedCode || existingCode || matchedCode,
        };
      });

    try {
      await updateCodes({
        id: selectedPurchaseOrder.id,
        body: { backOrderItemPatches },
      }).unwrap();

      const newSavedCodes: Record<string, string> = {};
      backOrderItemPatches.forEach((patch) => {
        newSavedCodes[patch.description] = patch.itemCode;
      });
      setSavedCodes((prev) => ({ ...prev, ...newSavedCodes }));
      setEditedCodes({});

      setSnackbar({
        open: true,
        message: 'Item codes updated successfully',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to update item codes',
        severity: 'error',
      });
    }
  };

  const renderRows = (items: RequisitionItem[], editable = false) =>
    items.map((item, index) => (
      <StyledTableRow key={`${item.description ?? 'item'}-${index}`}>
        <TableCell>{item.description}</TableCell>
        <TableCell>
          {editable && isStoresClerk
            ? savedCodes[item.description as string] ?? editedCodes[item.description as string] ?? item.itemCode
            : item.itemCode}
        </TableCell>
        {editable && isStoresClerk ? (
          <TableCell>
            <Autocomplete
              options={inventoryItems}
              getOptionLabel={(option) => option.description || ''}
              value={findBestInventoryMatch(item)}
              isOptionEqualToValue={(option, value) => option.itemCode === value.itemCode}
              onChange={(_, newValue) => handleAutocompleteChange(item.description as string, newValue)}
              sx={{ minWidth: 260, backgroundColor: '#fff', borderRadius: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Search Inventory Item"
                />
              )}
            />
          </TableCell>
        ) : null}
        <TableCell>
          {item.quantity?.quantityValue} {item.quantity?.quantityUnit}
        </TableCell>
        <TableCell>
          {formatCurrency(item.rate, selectedPurchaseOrder.quotation?.currency || 'usd')}
        </TableCell>
        <TableCell>
          {formatCurrency(item.discountPerItem, selectedPurchaseOrder.quotation?.currency || 'usd')}
        </TableCell>
        <TableCell>
          {formatCurrency(calculateVAT(item, selectedPurchaseOrder.date), selectedPurchaseOrder.quotation?.currency || 'usd')}
        </TableCell>
        <TableCell>
          {formatCurrency(item.totalCostPerItem, selectedPurchaseOrder.quotation?.currency || 'usd')}
        </TableCell>
      </StyledTableRow>
    ));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <GradientDialogTitle>Received VS Back Order Items</GradientDialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, color: 'primary.main' }}>
            <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
            Items Received
          </Typography>
          {itemsReceived?.length > 0 ? (
            <StyledTable>
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>
                    <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Description
                  </StyledTableCell>
                  <StyledTableCell>Item Code</StyledTableCell>
                  <StyledTableCell>
                    <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Quantity
                  </StyledTableCell>
                  <StyledTableCell>Unit Cost</StyledTableCell>
                  <StyledTableCell>Item Discount</StyledTableCell>
                  <StyledTableCell>VAT</StyledTableCell>
                  <StyledTableCell>
                    <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Total Cost
                  </StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>{renderRows(itemsReceived)}</TableBody>
            </StyledTable>
          ) : (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              No items received yet.
            </Typography>
          )}

          <Typography variant="h6" gutterBottom sx={{ mt: 4, color: 'error.main' }}>
            <Warning sx={{ verticalAlign: 'middle', mr: 1 }} />
            Backorder Items
          </Typography>
          {backOrderItems?.length > 0 ? (
            <StyledTable>
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>
                    <Description sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Description
                  </StyledTableCell>
                  <StyledTableCell>Item Code</StyledTableCell>
                  {isStoresClerk ? <StyledTableCell>Select Inventory Item</StyledTableCell> : null}
                  <StyledTableCell>
                    <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Quantity
                  </StyledTableCell>
                  <StyledTableCell>Unit Cost</StyledTableCell>
                  <StyledTableCell>Item Discount</StyledTableCell>
                  <StyledTableCell>VAT</StyledTableCell>
                  <StyledTableCell>
                    <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Total Cost
                  </StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>{renderRows(backOrderItems, true)}</TableBody>
            </StyledTable>
          ) : (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              No backorder items.
            </Typography>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          {isStoresClerk ? (
            <Button
              onClick={handleSaveCodes}
              variant="contained"
              color="success"
              disabled={isSaving}
              sx={{ borderRadius: '20px' }}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isSaving ? 'Saving...' : 'Update Item Codes'}
            </Button>
          ) : null}
          <Button onClick={onClose} variant="contained" color="primary" sx={{ borderRadius: '20px' }}>
            Close
          </Button>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default BackOrderAndReceivedComparisonDialog;